import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { undoableAction } from '@/frontend/components/undo/undoableAction'
import { Bell } from 'lucide-react'
import { PanelEmptyState } from '@/frontend/components/EmptyState'

interface Notification {
  id: string
  title: string
  body: string | null
  url: string | null
  event_name: string
  read: boolean
  created_at: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Props {
  page_title: string
  notifications: Notification[]
  pagination: Pagination
}

function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function handleMarkAllAsRead() {
  router.post('/dashboard/notifications/mark_all_as_read')
}

export default function NotificationsIndex({ page_title, notifications, pagination }: Props) {
  // Leitura otimista (SZ-1589): ids marcados como lidos na UI enquanto o POST
  // adiado do undoableAction ainda não foi enviado ao servidor.
  const [readOverrides, setReadOverrides] = useState<string[]>([])
  const isRead = (notification: Notification) => notification.read || readOverrides.includes(notification.id)
  const hasUnread = notifications.some((n) => !isRead(n))

  // Marca como lida com undo de 5s: a notificação apaga na hora e o request só
  // sai quando o toast expira; "Desfazer" restaura sem nada ter sido enviado.
  const handleMarkAsRead = (notification: Notification) => {
    undoableAction({
      message: 'Notificação marcada como lida',
      apply: () => setReadOverrides((ids) => [...ids, notification.id]),
      revert: () => setReadOverrides((ids) => ids.filter((id) => id !== notification.id)),
      commit: () => {
        router.post(`/dashboard/notifications/${notification.id}/mark_as_read`, {}, { preserveScroll: true })
      },
    })
  }

  return (
    <>
      <div>
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">{page_title}</h1>
            <p className="text-sm text-muted-foreground mt-1">Acompanhe alertas e mensagens da sua organização.</p>
          </div>
          {hasUnread && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-sm text-primary hover:text-primary transition-colors"
            >
              Marcar todas como lidas
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <PanelEmptyState
            icon={Bell}
            framed={false}
            title="Nenhuma notificação ainda"
            description="Você recebe um aviso aqui quando algo importante acontecer na sua organização."
          />
        ) : (
          <div className="space-y-1">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`flex items-start gap-4 py-3 px-4 rounded-lg transition-colors ${
                  isRead(notification) ? 'bg-background hover:bg-accent' : 'bg-primary/10 hover:bg-primary/15 dark:bg-primary/10 dark:hover:bg-primary/15'
                }`}
              >
                <div className="flex-shrink-0 mt-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${isRead(notification) ? 'bg-muted' : 'bg-primary'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      {notification.url ? (
                        <Link
                          href={notification.url}
                          className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                        >
                          {notification.title}
                        </Link>
                      ) : (
                        <p className="text-sm font-medium text-foreground">{notification.title}</p>
                      )}
                      {notification.body && (
                        <p className="text-sm text-muted-foreground mt-0.5">{notification.body}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(notification.created_at)}</p>
                    </div>
                    {!isRead(notification) && (
                      <button
                        onClick={() => handleMarkAsRead(notification)}
                        className="flex-shrink-0 text-xs text-muted-foreground hover:text-muted-foreground transition-colors"
                      >
                        Marcar como lida
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.total_pages > 1 && (
          <nav className="flex items-center justify-between mt-8 pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Pagina {pagination.current_page} de {pagination.total_pages} ({pagination.total_count} notificações)
            </div>
            <div className="flex gap-2">
              {pagination.current_page > 1 && (
                <Link
                  href={`/dashboard/notifications?page=${pagination.current_page - 1}`}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent transition-colors"
                >
                  Anterior
                </Link>
              )}
              {pagination.current_page < pagination.total_pages && (
                <Link
                  href={`/dashboard/notifications?page=${pagination.current_page + 1}`}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent transition-colors"
                >
                  Próxima
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </>
  )
}
